import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Close } from '@bb-ui/icons/dist/small/Close';

import { FeedbackForm } from './FeedbackForm';
import { useStyles } from './FeedbackWidget.styles';
import { SubmitErrorKind, SubmitStatus } from './useSubmitFeedback';
import { IFeedbackDraft } from './FeedbackWidget.types';

export interface IFeedbackPanelProps {
    panelId: string;
    titleId: string;
    title: string;
    status: SubmitStatus;
    errorKind?: SubmitErrorKind;
    draft: IFeedbackDraft;
    onDraftChange: (draft: IFeedbackDraft) => void;
    onSubmit: (rating: number, comment: string) => void;
    onClose: () => void;
}

/**
 * The dialog half of the widget, split out so its `useStyles()` runs inside the
 * dark BbThemeProvider; in the same component it would get the light theme.
 */
export const FeedbackPanel = React.forwardRef<
    HTMLDivElement,
    IFeedbackPanelProps
>(
    (
        {
            panelId,
            titleId,
            title,
            status,
            errorKind,
            draft,
            onDraftChange,
            onSubmit,
            onClose,
        },
        ref,
    ) => {
        const classes = useStyles();
        const { t } = useTranslation();

        return (
            <div
                ref={ref}
                id={panelId}
                tabIndex={-1}
                role="dialog"
                aria-modal="false"
                aria-labelledby={titleId}
                className={classes.panel}
                data-testid="feedback-panel"
            >
                <IconButton
                    className={classes.closeButton}
                    aria-label={t('feedback.widget.close')}
                    onClick={onClose}
                    data-testid="feedback-close"
                >
                    <Close />
                </IconButton>

                {status === 'success' ? (
                    <div
                        className={classes.thanks}
                        data-testid="feedback-thanks"
                    >
                        <Typography
                            variant="h3"
                            component="h2"
                            id={titleId}
                            className={classes.title}
                        >
                            {t('feedback.form.thanksTitle')}
                        </Typography>
                        <Typography
                            component="p"
                            role="status"
                            className={classes.thanksText}
                        >
                            {t('feedback.form.thanksText')}
                        </Typography>
                    </div>
                ) : (
                    <FeedbackForm
                        title={title}
                        status={status}
                        errorKind={errorKind}
                        draft={draft}
                        onDraftChange={onDraftChange}
                        onSubmit={onSubmit}
                        titleId={titleId}
                    />
                )}
            </div>
        );
    },
);

FeedbackPanel.displayName = 'FeedbackPanel';
